import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Move } from 'lucide-react';

/**
 * ImagePositionPicker
 * Shows a preview of the image and lets the admin drag to set object-position.
 * Props:
 *   src        - image URL
 *   position   - current position string e.g. "50% 30%"
 *   onChange   - called with new position string when drag ends
 */
export default function ImagePositionPicker({ src, position = '50% 50%', onChange }) {
  const containerRef = useRef(null);
  const isDragging   = useRef(false);

  // Parse "X% Y%" → [x, y] numbers 0-100
  const parsePos = (str) => {
    const [x = '50%', y = '50%'] = (str || '50% 50%').split(' ');
    return [parseFloat(x), parseFloat(y)];
  };

  const [pos, setPos] = useState(() => parsePos(position));

  // Keep in sync if parent changes position externally
  useEffect(() => { setPos(parsePos(position)); }, [position]);

  const calcPos = useCallback((clientX, clientY) => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const x = Math.round(Math.min(100, Math.max(0, ((clientX - rect.left) / rect.width) * 100)));
    const y = Math.round(Math.min(100, Math.max(0, ((clientY - rect.top)  / rect.height) * 100)));
    const newPos = [x, y];
    setPos(newPos);
    return newPos;
  }, []);

  const handleMouseDown = (e) => {
    e.preventDefault();
    isDragging.current = true;
    calcPos(e.clientX, e.clientY);
  };

  const handleMouseMove = useCallback((e) => {
    if (!isDragging.current) return;
    calcPos(e.clientX, e.clientY);
  }, [calcPos]);

  const handleMouseUp = useCallback((e) => {
    if (!isDragging.current) return;
    isDragging.current = false;
    const newPos = calcPos(e.clientX, e.clientY);
    if (newPos) onChange?.(`${newPos[0]}% ${newPos[1]}%`);
  }, [calcPos, onChange]);

  // Touch support
  const handleTouchStart = (e) => {
    isDragging.current = true;
    const t = e.touches[0];
    calcPos(t.clientX, t.clientY);
  };
  const handleTouchMove = useCallback((e) => {
    if (!isDragging.current) return;
    const t = e.touches[0];
    calcPos(t.clientX, t.clientY);
  }, [calcPos]);
  const handleTouchEnd = useCallback((e) => {
    if (!isDragging.current) return;
    isDragging.current = false;
    const t = e.changedTouches[0];
    const newPos = calcPos(t.clientX, t.clientY);
    if (newPos) onChange?.(`${newPos[0]}% ${newPos[1]}%`);
  }, [calcPos, onChange]);

  useEffect(() => {
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    window.addEventListener('touchmove', handleTouchMove, { passive: true });
    window.addEventListener('touchend', handleTouchEnd);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleTouchEnd);
    };
  }, [handleMouseMove, handleMouseUp, handleTouchMove, handleTouchEnd]);

  if (!src) return null;

  const posStr = `${pos[0]}% ${pos[1]}%`;

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <Move size={14} className="text-yellow-600" />
        <span className="text-xs font-medium text-gray-600">Drag to set focal point</span>
        <span className="ml-auto text-xs font-mono text-gray-400 bg-gray-100 px-2 py-0.5 rounded">
          {pos[0]}% / {pos[1]}%
        </span>
      </div>

      {/* Draggable preview */}
      <div
        ref={containerRef}
        onMouseDown={handleMouseDown}
        onTouchStart={handleTouchStart}
        className="relative w-full h-40 overflow-hidden rounded-lg border-2 border-yellow-400 cursor-crosshair select-none"
        title="Drag to select which part of the image is shown"
        style={{ userSelect: 'none' }}>
        <img
          src={src} alt="preview"
          className="absolute inset-0 w-full h-full"
          style={{ objectFit: 'cover', objectPosition: posStr, pointerEvents: 'none' }}
          draggable={false}
        />
        {/* Crosshair marker */}
        <div
          className="absolute w-6 h-6 -translate-x-1/2 -translate-y-1/2 pointer-events-none"
          style={{ left: `${pos[0]}%`, top: `${pos[1]}%` }}>
          <div className="absolute inset-0 rounded-full border-2 border-white shadow-lg bg-yellow-500/40" />
          <div className="absolute top-1/2 left-0 right-0 h-px bg-white -translate-y-1/2" />
          <div className="absolute left-1/2 top-0 bottom-0 w-px bg-white -translate-x-1/2" />
        </div>
        {/* Hint overlay */}
        <div className="absolute bottom-1 right-1 bg-black/50 text-white text-[10px] px-1.5 py-0.5 rounded pointer-events-none">
          Drag to reposition
        </div>
      </div>

      {/* Live preview box showing how it looks on front-end */}
      <div className="space-y-1">
        <p className="text-xs text-gray-400">Front-end preview (16:5 ratio)</p>
        <div className="w-full h-20 overflow-hidden rounded border border-gray-200">
          <img src={src} alt="front-end preview"
            className="w-full h-full"
            style={{ objectFit: 'cover', objectPosition: posStr }}
          />
        </div>
      </div>
    </div>
  );
}
